import { IIndicator, IInsight } from '@jumpstart/core';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import React, { useState } from 'react';
import ScoutErrorAlert from '../../../widgets/ScoutErrorAlert';
import { Transition } from './EventsList';
import IndicatorEditor from './IndicatorEditor';
import { IValidation, validateIndicator } from './utils';


export interface IIndicatorsList {
  event: IInsight;
  selected: IIndicator | null;
  maxId: number;
  validation: IValidation;

  onSelect(indicator: IIndicator & { index?: number }): void;

  onAdd(indicator: IIndicator & { index?: number }): void;

  onDelete(indicator: IIndicator & { index?: number }): void;
}

export default function IndicatorsList(props: IIndicatorsList) {
  const { event, onSelect, onDelete, selected, onAdd, maxId, validation } = props;
  const [anchor, setAnchor] = useState<null | { element: HTMLElement, indicator: IIndicator & { index?: number } }>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [staged, setStaged] = useState<IIndicator & { index?: number } | null>(null);
  const [adding, setAdding] = useState<{ indicator: IIndicator & { index?: number }; changed: boolean } | null>(null);

  return (
    <Grid container direction="column" wrap="nowrap">
      <ScoutErrorAlert error={error} setError={setError}/>
      <List dense disablePadding>
        <React.Fragment>
          {event.content.indicators
            .map((indicator: IIndicator) => {
              return {
                indicator,
                validation: validateIndicator(indicator)
              };
            })
            .map(({ indicator, validation }) =>
              <ListItem
                key={indicator.id}
                button
                selected={!!staged ? staged.id === indicator.id : false}
                onClick={() => {
                  onSelect(indicator);
                }}
              >
                <ListItemText
                  id={event.id}
                  primary={indicator.name}
                  secondary={indicator.secondary}
                  secondaryTypographyProps={{ style: { fontSize: 10 } }}
                />
                <ListItemSecondaryAction>
                  <Grid container direction="row" alignItems="center">
                    {
                      [
                        {
                          icon: '⚠️',
                          severity: 'warning',
                          amount: validation.numWarnings
                        },
                        {
                          icon: '❗',
                          severity: 'error',
                          amount: validation.numErrors
                        }

                      ]
                        .filter(({ amount }) => amount > 0)
                        .map(({ icon, severity, amount }) =>
                          <Button
                            disabled
                            style={{
                              width: 32,
                              minWidth: 32
                            }}
                          >
                            {icon}{amount}
                          </Button>
                        )
                    }
                    <IconButton
                      aria-label="more"
                      aria-controls="long-menu"
                      aria-haspopup="true"
                      onClick={(e) => setAnchor({ element: e.currentTarget, indicator: indicator })}
                    >
                      <MoreHorizIcon/>
                    </IconButton>
                  </Grid>
                </ListItemSecondaryAction>
              </ListItem>
            )}
        </React.Fragment>
        <ListItem button onClick={() => setAdding({
          indicator: {
            id: 'DEFAULT',
            name: '',
            description: '',
            dialog: {
              id: '929',
              body: '',
              responses: {}
            },
            index: maxId + 1
          },
          changed: false
        })}><ListItemText>+ New Indicator</ListItemText></ListItem>
      </List>
      {loading ? <CircularProgress size={16}/> : null}

      <Menu
        anchorEl={anchor && anchor.element}
        keepMounted
        open={!!anchor}
        onClose={() => setAnchor(null)}
        PaperProps={{
          style: {
            maxHeight: 48 * 4.5,
            width: '20ch'
          }
        }}
      >
        <MenuItem onClick={() => {
          if (anchor) {
            setStaged(anchor.indicator);
            setAnchor(null);
          }
        }}>
          Delete
        </MenuItem>
        <MenuItem onClick={() => {
          if (anchor) {
            onAdd({ ...anchor.indicator, index: event.content.indicators.length });
            setAnchor(null);
          }
        }}>
          Copy
        </MenuItem>
        <MenuItem onClick={() => {
          setAnchor(null);
        }}>Close</MenuItem>
      </Menu>

      <Dialog
        TransitionComponent={Transition}
        open={!!staged}
        onClose={() => setStaged(null)}
      >
        <DialogTitle>Remove "{staged && staged.name}"?</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this indicator?<br/><br/><b>This will erase all content
            data</b></DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setStaged(null)}>
            No
          </Button>
          <Button onClick={() => {
            if (staged) {
              onDelete(staged);
            }
          }}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        PaperProps={{ style: { height: '100%' } }}
        fullScreen
        TransitionComponent={Transition}
        keepMounted
        open={!!adding}
      >
        <DialogActions
          style={{ width: '100%', position: 'absolute' }}
        >
          <IconButton onClick={() => {
            setAdding(null);
          }}><CloseIcon/></IconButton>
        </DialogActions>
        <DialogContent
          sx={{
            padding: 12,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            width: '100%',
            '& > *': {
              maxWidth: 550
            }
          }}
        >
          {adding && <IndicatorEditor
            validation={validation}
            event={event}
            indicator={adding.indicator}
            loading={loading}
            max={maxId + 1}
            showPreview={false}
            onSubmit={(_newIndicator) => {
              onAdd(_newIndicator);
              setAdding(null);
            }}
          />
          }
        </DialogContent>
      </Dialog>
    </Grid>
  );
}
